import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { useLanguage } from "../../context/LanguageContext";
import api from "../../lib/api.ts";
// currency helpers
import { useCurrencyUtils } from "../../utils/currencyUtils";

const SellerPlans = () => {
  const { user } = useAuth();

  const { translate, language } = useLanguage();
  const isRTL = language === "ar";
  const { convertPrice, displayPriceWithSymbol } = useCurrencyUtils();
  const [packageHistory, setPackageHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");

  // Load initial data
  useEffect(() => {
    if (user?.seller?.id) {
      fetchSellerPackageHistory();
    }
  }, [user?.seller?.id]);

  const fetchSellerPackageHistory = async () => {
    if (!user?.seller?.id) return;

    setLoading(true);
    setError(null);

    try {
      const response = await api.get("/sellerpackagehistories/paginated", {
        params: {
          page: 1,
          limit: 50,
          orderBy: "id",
          order: "DESC",
          search: "",
          slug: "",
          sellerId: user?.seller?.id || user?.sellerId,
        },
      });

      console.log("Package history response:", response.data);

      // Handle different possible response structures
      let data = response.data;
      if (data && Array.isArray(data)) {
        setPackageHistory(data);
      }
      //  else if (data && Array.isArray(data.data)) {
      //   setPackageHistory(data.data);
      // } 
      // wednesday fix - to show only active package  in seller profile page
      else if (data && Array.isArray(data.data)) {
        const activePackages = data.data.filter((pkg) => pkg.isActive);

        const latestActive = activePackages.sort(
          (a, b) => new Date(b.approvedAt) - new Date(a.approvedAt),
        )[0];

        setPackageHistory(latestActive ? [latestActive] : []);
      }
      // ============================ e===========
      else if (data && Array.isArray(data.items)) {
        setPackageHistory(data.items);
      } else {
        console.log("Unexpected response structure:", data);
        setPackageHistory([]);
      }
    } catch (err) {
      console.error("Error fetching seller package history:", err);
      setError(translate("sellerPlans.messages.failedToLoad"));
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString(
      language === "ar" ? "ar-AE" : "en-US",
      {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      },
    );
  };

  const getStatusBadge = (isActive, isApproved) => {
    if (isActive && isApproved) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-700 text-white">
          {translate("sellerPlans.status.active")}
        </span>
      );
    } else if (isApproved && !isActive) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-700 text-white">
          {translate("sellerPlans.status.inactive")}
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-700 text-white">
          {translate("sellerPlans.status.pending")}
        </span>
      );
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white shadow-md rounded-[12px] p-6">
        <div className="text-center text-red-600">
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="bg-card rounded-lg shadow-md"
      dir={isRTL ? "rtl" : "ltr"}
      lang={language}
    >
      {/* Header */}
      <div className="bg-card font-medium rounded-t-[8px] sm:rounded-t-[16px]">
        {/* Standardized container with fixed height and consistent padding */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between px-4 sm:px-5 py-3 sm:py-4 rounded-t-[8px] sm:rounded-t-[12px] bg-primary-soft min-h-[120px] lg:min-h-[72px]">
          <div className="flex flex-col justify-center">
            <h2 className="text-white text-base sm:text-lg font-semibold whitespace-nowrap">
              {translate("sellerPlans.heading")}
            </h2>
          </div>

          {/* Right side spacer to maintain layout consistency with pages that have search/filters */}
          <div className="hidden lg:block"></div>
        </div>
      </div>

      {/* Remove tabs section since we only have one view now */}

      <div className="px-5">
        {successMessage && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-3 my-4 text-green-700">
            {successMessage}
          </div>
        )}
        {/* Show only My Packages view */}
        {!Array.isArray(packageHistory) || packageHistory.length === 0 ? (
          <div className="text-center py-8">
            <div className="text-gray-400 mb-4">
              <svg
                className="mx-auto h-12 w-12"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-white mb-2">
              {translate("sellerPlans.messages.noPackageHistory")}
            </h3>
            <p className="text-description">
              {translate("sellerPlans.messages.noPackageHistoryYet")}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 py-6">
            {packageHistory.map((pkg) => (
              <div
                key={pkg.id}
                className="bg-page rounded-lg shadow-lg border border-default overflow-hidden"
              >
                {/* Card Header */}
                <div className="relative p-6">
                  {/* Package Name */}
                  <h3 className="text-xl font-bold text-white mb-2">
                    {pkg.sellerPackage?.name}
                  </h3>

                  {/* Location */}
                  {pkg.sellerPackage?.location?.name && (
                    <div
                      className={`flex items-center text-white/70 mb-4 ${
                        isRTL ? "flex-row-reverse" : ""
                      }`}
                    >
                      <svg
                        className={`w-4 h-4 text-red-500 ${
                          isRTL ? "ml-2" : "mr-2"
                        }`}
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <span className="text-sm">
                        {/* {translate("sellerPlans.labels.location")} */}
                        {pkg.sellerPackage?.location?.name}
                      </span>
                    </div>
                  )}

                  {/* Badge */}
                  <div
                    className={`absolute top-4 ${isRTL ? "left-4" : "right-4"}`}
                  >
                    <span
                      className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium text-white ${
                        pkg.sellerPackage?.name?.includes("Corporate")
                          ? "bg-green-500"
                          : "bg-blue-500"
                      }`}
                    >
                      {user.seller.sellerTypeId === 2
                        ? translate("sellerPlans.labels.corporate")
                        : translate("sellerPlans.labels.individual")}
                    </span>
                  </div>
                </div>

                {/* Features Section */}
                <div className="px-6 pb-4">
                  <h4 className="text-lg font-semibold text-white mb-2">
                    {translate("sellerPlans.labels.features")}
                  </h4>
                  <p className="text-sm text-white/70 mb-4">
                    {translate("sellerPlans.labels.includedIn")}{" "}
                    {user.seller.sellerTypeId === 2
                      ? translate("sellerPlans.labels.corporate")
                      : translate("sellerPlans.labels.individual")}{" "}
                    {translate("sellerPlans.labels.plan")}
                  </p>

                  <div className="space-y-2">
                    <div
                      className={`flex items-center ${
                        isRTL ? "flex-row-reverse" : ""
                      }`}
                    >
                      <svg
                        className={`w-4 h-4 text-green-500 ${
                          isRTL ? "ml-3" : "mr-3"
                        }`}
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <span className="text-sm text-white/70">
                        {translate("sellerPlans.labels.listingFee")}{" "}
                        {displayPriceWithSymbol(
                          convertPrice(pkg.sellerPackage?.listingFee || 0),
                        )}
                      </span>
                    </div>
                    <div
                      className={`flex items-center ${
                        isRTL ? "flex-row-reverse" : ""
                      }`}
                    >
                      <svg
                        className={`w-4 h-4 text-green-500 ${
                          isRTL ? "ml-3" : "mr-3"
                        }`}
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <span className="text-sm text-white/70">
                        {translate("sellerPlans.labels.relistingFee")}{" "}
                        {displayPriceWithSymbol(
                          convertPrice(pkg.sellerPackage?.reListingFee || 0),
                        )}
                      </span>
                    </div>
                    <div
                      className={`flex items-center ${
                        isRTL ? "flex-row-reverse" : ""
                      }`}
                    >
                      <svg
                        className={`w-4 h-4 text-green-500 ${
                          isRTL ? "ml-3" : "mr-3"
                        }`}
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <span className="text-sm text-white/70">
                        {translate("sellerPlans.labels.numberOfRuns")}{" "}
                        {pkg.sellerPackage?.numberOfRuns || 0}
                      </span>
                    </div>
                    <div
                      className={`flex items-center ${
                        isRTL ? "flex-row-reverse" : ""
                      }`}
                    >
                      <svg
                        className={`w-4 h-4 text-green-500 ${
                          isRTL ? "ml-3" : "mr-3"
                        }`}
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <span className="text-sm text-white/70">
                        {translate(
                          "sellerPlans.labels.fineAfterFreeParkingDays",
                        )}{" "}
                        {displayPriceWithSymbol(
                          convertPrice(
                            pkg.sellerPackage?.fineAfterFreeParkingDays || 0,
                          ),
                        )}
                      </span>
                    </div>
                    <div
                      className={`flex items-center ${
                        isRTL ? "flex-row-reverse" : ""
                      }`}
                    >
                      <svg
                        className={`w-4 h-4 text-green-500 ${
                          isRTL ? "ml-3" : "mr-3"
                        }`}
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <span className="text-sm text-white/70">
                        {translate("sellerPlans.labels.freeParkingDays")}{" "}
                        {pkg.sellerPackage?.freeParkingDays || 0}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Fee Range Table */}
                <div className="px-6 pb-4">
                  <div className="overflow-hidden  border border-gray-500">
                    <table className="min-w-full divide-y divide-page">
                      <thead className="bg-page text-white">
                        <tr>
                          <th
                            className={`px-4 py-3 ${
                              isRTL ? "text-right" : "text-left"
                            } text-xs font-medium uppercase tracking-wider`}
                          >
                            {translate("sellerPlans.labels.fee")}
                          </th>
                          <th
                            className={`px-4 py-3 ${
                              isRTL ? "text-right" : "text-left"
                            } text-xs font-medium uppercase tracking-wider`}
                          >
                            {translate("sellerPlans.labels.startingRange")}
                          </th>
                          {/* <th
        className={`px-4 py-3 ${isRTL ? "text-right" : "text-left"} text-xs font-medium uppercase tracking-wider`}
      >
        {translate("sellerPlans.labels.toRange")}
      </th> */}
                        </tr>
                      </thead>
                      <tbody className="bg-card divide-y divide-page">
                        {pkg.sellerPackage?.sellerPackageRanges?.map(
                          (range, index) => (
                            <tr
                              key={range.id || index}
                              className="hover:bg-page"
                            >
                              <td className="px-4 py-3 text-sm text-white">
                                <div className="flex justify-between">
                                  <span>
                                    {displayPriceWithSymbol(
                                      convertPrice(range.fee),
                                    )}
                                  </span>
                                </div>
                              </td>
                              {/* <td className='px-4 py-3 text-sm text-white'>
          
        </td> */}
                              <td className="px-4 py-3 text-sm text-white">
                                <div className="flex justify-between">
                                  <span>
                                    {displayPriceWithSymbol(
                                      convertPrice(range.startRange),
                                    )}
                                    -{" "}
                                    {displayPriceWithSymbol(
                                      convertPrice(range.toRange),
                                    )}
                                  </span>
                                </div>
                              </td>
                            </tr>
                          ),
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Status Information */}
                <div className="bg-primary-soft px-6 py-3 border-t border-default">
                  <div
                    className={`flex justify-between items-center ${
                      isRTL ? "flex-row-reverse" : ""
                    }`}
                  >
                    <div className="text-xs text-white">
                      {translate("sellerPlans.labels.status")}{" "}
                      {getStatusBadge(pkg.isActive, pkg.isApproved)}
                    </div>
                    <div className="text-xs text-white/70">
                      {translate("sellerPlans.labels.approved")}{" "}
                      {formatDate(pkg.approvedAt)}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SellerPlans;
